import { forwardRef } from 'react';
import { cn } from '@/utils/helpers';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps {
  label?: string;
  name: string;
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  orientation?: 'vertical' | 'horizontal';
  cardStyle?: boolean;
  required?: boolean;
  disableHover?: boolean;
}

const RadioGroup = forwardRef<HTMLInputElement, RadioGroupProps>(
  (
    {
      label,
      name,
      options,
      value,
      onChange,
      error,
      orientation = 'vertical',
      cardStyle = false,
      required = false,
      disableHover = false,
    },
    ref
  ) => {
    const handleChange = (optionValue: string) => {
      if (onChange) {
        onChange(optionValue);
      }
    };

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            {label}
            {required && <span className="text-error-600 ml-1">*</span>}
          </label>
        )}

        <div
          className={cn(
            'flex gap-3',
            orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap'
          )}
          role="radiogroup"
          aria-label={label}
        >
          {options.map((option, index) => {
            const isSelected = value === option.value;
            const isDisabled = option.disabled;
            const inputId = `${name}-${option.value}`;

            return (
              <div key={option.value} className="relative">
                <input
                  ref={index === 0 ? ref : undefined}
                  type="radio"
                  id={inputId}
                  name={name}
                  value={option.value}
                  checked={isSelected}
                  onChange={() => handleChange(option.value)}
                  disabled={isDisabled}
                  className="sr-only peer"
                  aria-describedby={error ? `${name}-error` : undefined}
                />
                <label
                  htmlFor={inputId}
                  className={cn(
                    'flex items-start gap-3 transition-all duration-200',
                    disableHover ? 'cursor-not-allowed' : 'cursor-pointer',
                    cardStyle
                      ? cn(
                          'p-4 border-2 rounded-lg',
                          isSelected
                            ? 'border-primary-600 bg-primary-50 shadow-sm'
                            : 'border-neutral-300 bg-white hover:border-neutral-400',
                          isDisabled && 'opacity-50 cursor-not-allowed'
                        )
                      : 'py-2'
                  )}
                >
                  {/* Custom Radio Circle */}
                  {!cardStyle && (
                    <div
                      className={cn(
                        'flex-shrink-0 w-5 h-5 rounded-full border-2 transition-all duration-200 mt-0.5',
                        isSelected
                          ? 'border-primary-600 bg-primary-600'
                          : 'border-neutral-300 bg-white',
                        !isDisabled && !disableHover && 'hover:border-primary-400'
                      )}
                    >
                      {isSelected && (
                        <div className="w-full h-full rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-white rounded-full" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Icon */}
                  {option.icon && (
                    <div className="flex-shrink-0 text-neutral-600">
                      {option.icon}
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div
                      className={cn(
                        'text-sm font-medium',
                        isSelected ? 'text-primary-900' : 'text-neutral-900'
                      )}
                    >
                      {option.label}
                    </div>
                    {option.description && (
                      <div className="text-sm text-neutral-500 mt-0.5">
                        {option.description}
                      </div>
                    )}
                  </div>

                  {/* Check Icon for Card Style */}
                  {cardStyle && isSelected && (
                    <div className="flex-shrink-0 text-primary-600">
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                </label>
              </div>
            );
          })}
        </div>

        {error && (
          <p id={`${name}-error`} className="mt-2 text-sm text-error-600" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
